import { useId, useState } from 'react';
import { Micro, SerifHeading } from '../primitives';
import type { WeeklyPlan, Day } from '../../fixtures/plan';
import { sessionCounts } from '../today/DaysDoneRing';
import { DayDetails } from './DayDetails';
import { WeekListV2 } from '../today/v2/WeekListV2';
import styles from './WeekPlanDesktop.module.css';

/** A token that is only dashes and spaces is dropped from a spoken label. */
const PLACEHOLDER_TOKEN = /^[\s–—-]*$/;

type WeekPlanDesktopProps = {
  plan: WeeklyPlan;
  /**
   * Phase 6c.1 — when provided, each row with an `id` is a click target.
   * B309: the click OPENS the day's details (today included) and never
   * writes; the details panel's done / not done button is the only caller of
   * `onToggle(dayId, currentStatus)`. The panel renders after the last row, so
   * opening, switching and closing it never moves a row.
   *
   * Rows without an `id` (demo / fixture) stay inert.
   */
  onToggle?: (dayId: string, currentStatus: Day['completionStatus']) => Promise<unknown>;
  /**
   * B78 — web-absolute destination for the "Full plan →" link (the house
   * `/v2/…` anchor pattern; NativeLinkGuard reroutes it in the shell).
   * Absent → the link is not rendered at all: a link without a destination
   * is a dead control, not an affordance.
   */
  fullPlanHref?: string;
  /**
   * W1-TODAY-WEEK (TW-5, frame 1v) — `'v2'` renders the 2.0 desktop week card: "This
   * week", the one week measure (DM Sans, no serif), rows "Mon 12" through "Sun 18",
   * and the same day panels and future-day rule as the phone list (`WeekListV2`).
   * Absent: the card exactly as before.
   */
  variant?: 'v2';
};

/**
 * Desktop variant of the weekly plan. Vertical list (one row per day)
 * instead of mobile's 7-col grid. Header shows "This week · date range"
 * + "Full plan →" link + mileage-done / go-to metadata.
 *
 * Phase 6c.1: completed rows get a ✓ checkmark on the right + soft
 * warm-tinted background. B309: click a row to open that day's details;
 * done / not done is a button inside them.
 *
 * Today row renders with --ink-900 background and --today yellow mileage
 * value; hard days show label in --today-deep on light (rest-day background
 * is the cream surface).
 */
export function WeekPlanDesktop({ plan, onToggle, fullPlanHref, variant }: WeekPlanDesktopProps) {
  // B309 — the selected day's plan_day id. Clicking it again closes the panel.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const panelId = useId();
  const selectedDay = selectedId ? (plan.days.find((d) => d.id === selectedId) ?? null) : null;

  // W1-TODAY-WEEK — after the hooks above, so the hook order never depends on the variant.
  if (variant === 'v2') {
    return <WeekListV2 plan={plan} onToggle={onToggle} showDayNumber fullPlanHref={fullPlanHref} />;
  }
  // W3 — the mileage-independent sessions tally, beside the mileage one.
  const { completed, sessions } = sessionCounts(plan.days);
  // B338 — the header speaks the athlete's own unit. `weekMileage*` are miles for every
  // athlete; a km athlete's figures come from the adapter's `weekDistance*` pair, so the
  // header agrees with the "N km planned" status beside it. A mile athlete (or a plan with
  // no unit, the demo fixture) reads the miles figures, byte for byte.
  const useKm =
    plan.distanceUnit === 'km' &&
    typeof plan.weekDistanceDone === 'number' &&
    typeof plan.weekDistanceTarget === 'number';
  const headDone = useKm ? plan.weekDistanceDone : plan.weekMileageDone;
  const headTarget = useKm ? plan.weekDistanceTarget : plan.weekMileageTarget;

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <div>
          <Micro as="div">This week</Micro>
          <SerifHeading level="sm" as="div" className={styles.dates}>
            {plan.dateRange}
          </SerifHeading>
          {/* B309 — discoverability hint. Renders only on the real-data
              path (`onToggle` set). Demo fixtures have no clickable rows so
              the hint would be misleading. Same device-neutral line the
              season calendar uses for its day details. */}
          {onToggle && (
            <div className={styles.hint}>
              Choose a day for details
            </div>
          )}
        </div>
        {fullPlanHref && (
          <a className={styles.link} href={fullPlanHref}>Full plan →</a>
        )}
      </div>
      <div className={styles.meta}>
        {/* B28 — an engine week authored entirely in TIME has no mileage, and
            this rendered "0 / 0 mi" over five real sessions. Distance is only
            claimed when there is distance; the sessions counter and the status
            line (which now states a time total) carry an all-minutes week. */}
        {plan.weekMileageTarget > 0 && (
          <span className={`${styles.metaValue} serif tnum`}>
            {headDone}
            <span className={styles.metaTarget}> / {headTarget} {useKm ? 'km' : 'mi'}</span>
          </span>
        )}
        {/* W3 — sessions completed, mileage-independent (a 0-mi strength
            day still counts here even though it moves no miles). */}
        <span className={styles.metaSessions}>
          {plan.weekMileageTarget > 0 ? '· ' : ''}
          <b className="tnum">{completed}</b> of <span className="tnum">{sessions}</span> sessions
        </span>
        <span className={styles.metaStatus}>{plan.status}</span>
      </div>

      <div className={styles.list}>
        {plan.days.map((d) => {
          const isRest = d.rest ?? d.mileage === '—';
          const isCompleted = d.completionStatus === 'completed';
          const isSkipped = d.completionStatus === 'skipped';
          // B309 — every row with a plan row opens its details, today
          // included. The click never writes; completion is the details
          // button (the hero's <MarkTodayCompleteButton> stays as well).
          const isClickable = !!onToggle && !!d.id;
          const isSelected = isClickable && d.id === selectedId;

          const className = [
            styles.row,
            d.today ? styles.today : '',
            isRest ? styles.rest : '',
            d.hard ? styles.hard : '',
            isCompleted ? styles.completed : '',
            isSkipped ? styles.skipped : '',
            isClickable ? styles.clickable : '',
            isSelected ? styles.selected : '',
          ]
            .filter(Boolean)
            .join(' ');

          // B-19 — the value cell shows the "X of Y mi" pairing when a stored
          // actual exists (flag ON), else `d.mileage` unchanged.
          const valueText = d.actual ? d.actual.copy : d.mileage;

          // A screen reader SPEAKS this label: commas, never a dash, and the
          // placeholder "—" of a rest day is left out. Selection is carried by
          // aria-expanded below, not by words.
          const ariaLabel = isClickable
            ? `${[`${d.dayShort} ${d.dayNumber}`, d.label, valueText]
                .filter((t) => !PLACEHOLDER_TOKEN.test(t))
                .join(', ')}${isCompleted ? ', completed' : ''}`
            : undefined;

          const content = (
            <>
              <div className={styles.date}>
                <div className={styles.dayShort}>{d.dayShort}</div>
                <div className={`${styles.dayNum} serif tnum`}>{d.dayNumber}</div>
              </div>
              <div className={styles.body}>
                <div className={styles.label}>{d.label}</div>
                {d.pace && <div className={styles.pace}>{d.pace}</div>}
              </div>
              <div className={`${styles.value} serif tnum`}>{valueText}</div>
              {/* Phase 6c.1 follow-up — replace tiny corner ✓ with a
                  prominent "Done" chip on the right edge. Reads from
                  across the room ("user feedback: tiny check mark not
                  very obvious"). The chip lives in the same horizontal
                  flow as `.value` (mileage) — sits to its right.

                  B-40 BUG 6 — the Done chip and the (optional) RPE badge
                  share ONE grid cell (.doneCol) instead of each being a
                  direct grid item. The `.row` grid is 4 columns; two
                  separate chips overflowed into a 5th, implicit row →
                  the completed-with-RPE row rendered taller (founder
                  report). Wrapping keeps the grid at 4 columns, so a
                  completed row WITHOUT an RPE badge is pixel-identical to
                  before (the wrapper hugs the single Done chip in col 4),
                  and the RPE badge now sits beside Done on one line. */}
              {isCompleted && (
                <span className={styles.doneCol}>
                  <span className={styles.doneChip} aria-hidden="true">
                    <span className={styles.doneCheck}>✓</span>
                    <span className={styles.doneLabel}>Done</span>
                  </span>
                  {/* RPE — effort badge beside Done (vanilla rpe-badge
                      parity). Display only: capture/recovery on the hero. */}
                  {typeof d.rpe === 'number' && (
                    <span className={styles.rpeChip} aria-label={`Effort ${d.rpe} of 10`}>
                      {d.rpe}/10
                    </span>
                  )}
                </span>
              )}
              {d.today && <div className={styles.pin} aria-hidden />}
            </>
          );

          if (isClickable) {
            return (
              <button
                key={d.dayShort}
                type="button"
                className={className}
                onClick={() => setSelectedId((prev) => (prev === d.id ? null : d.id!))}
                aria-label={ariaLabel}
                aria-expanded={isSelected}
                aria-controls={isSelected ? panelId : undefined}
              >
                {content}
              </button>
            );
          }
          return (
            <div key={d.dayShort} className={className}>
              {content}
            </div>
          );
        })}
        {/* B309 fix round — ONE panel slot, after the last row. An under-the-row
            panel pushed every row below it down when it opened and pulled them
            back up when it closed (about 120 px on a switch), so the next click
            landed on the wrong day. Below the list, nothing above it can move. */}
        {selectedDay && (
          <DayDetails key={selectedDay.id} day={selectedDay} onToggle={onToggle} id={panelId} />
        )}
      </div>
    </div>
  );
}
